import * as React from 'react';
import { cn } from '../utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { Label } from './Label';
import { useTranslation } from '../providers/UIProvider';

const inputWrapperVariants = cva(
  `
    flex flex-col
  `,
  {
    variants: {
      labelSize: {
        xs: '',
        sm: '',
        md: '',
        lg: '',
        xl: ''
      }
    },
    defaultVariants: {
      labelSize: 'md'
    }
  }
);

type InputWrapperVariants = VariantProps<typeof inputWrapperVariants>;

export interface InputWrapperProps extends InputWrapperVariants {
  className?: string;
  children?: React.ReactNode;
  htmlFor?: string;
  label?: string;
  locKey?: string;
  isError?: boolean;
  errorText?: string | null;
  hint?: string;
  required?: boolean;
}

export function InputWrapper(props: InputWrapperProps) {
  const t = useTranslation();
  const autoId = React.useId();
  const {
    className,
    labelSize,
    label,
    locKey,
    htmlFor,
    children,
    hint,
    isError,
    errorText,
    required = false
  } = props;
  const translatedLabel = locKey ? t(`labels.${locKey}`) : label;
  const inputId = htmlFor ?? autoId;
  const errorId = isError && errorText ? `${inputId}-error` : undefined;
  const hintId = hint && !errorId ? `${inputId}-hint` : undefined;
  const describedBy = errorId ?? hintId;

  return (
    <div className={cn(inputWrapperVariants({ className, labelSize }), 'group')}>
      {translatedLabel && (
        <Label size={labelSize} className='flex gap-sm text-foreground-muted' htmlFor={inputId}>
          {translatedLabel}
          {required && <span className='text-error-text'>*</span>}
        </Label>
      )}
      {/* Clone children and apply id + error class + ARIA attributes to inputs */}
      {React.Children.map(children, child => {
        if (React.isValidElement(child)) {
          const existingClassName = (child.props as { className?: string }).className || '';
          const extraProps: Record<string, unknown> = {
            className: cn(existingClassName)
          };

          // Propagate id for label-input association (only if child doesn't already have one)
          if (!(child.props as { id?: string }).id) {
            extraProps.id = inputId;
          }

          // Only pass hasError to custom components
          if (typeof child.type !== 'string') {
            extraProps.hasError = isError;
          }

          // Propagate ARIA attributes for accessibility
          if (describedBy) {
            extraProps['aria-describedby'] = describedBy;
          }
          if (isError) {
            extraProps['aria-invalid'] = true;
          }
          if (required) {
            extraProps['aria-required'] = true;
          }

          return React.cloneElement(child, extraProps);
        }
        return child;
      })}

      {/* Message under input */}
      {errorText && isError ? (
        <p id={errorId} className='text-sm text-error-text transition-all'>
          {errorText}
        </p>
      ) : hint ? (
        <p id={hintId} className='text-sm text-foreground-muted'>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

InputWrapper.displayName = 'InputWrapper';
